import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowDownRight, ArrowUpRight, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { toInputDateValue } from '../../../utils/dateFormat';

const WEEK_DAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

export const addDays = (value, amount) => {
  const nextDate = new Date(value);
  nextDate.setDate(nextDate.getDate() + amount);
  return nextDate;
};

export const datesWithinRange = (startDate, endDate) => {
  const dates = [];
  let cursor = new Date(startDate);
  while (cursor < endDate) {
    dates.push(toInputDateValue(cursor));
    cursor = addDays(cursor, 1);
  }
  return dates;
};

export const buildBlockedDateSet = (blockedRanges) => {
  const blockedDates = new Set();
  blockedRanges.forEach((range) => {
    const startDate = new Date(range.check_in_date);
    const endDate = new Date(range.checkout_date);
    if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) return;
    datesWithinRange(startDate, endDate).forEach((dateValue) => blockedDates.add(dateValue));
  });
  return blockedDates;
};

// Just the FIRST day of each existing/synced reservation. Rendered as a green dot
// (instead of the generic red block dot) so hosts can spot where reservations
// actually begin, even in a fully-blocked stretch of the calendar.
export const buildBlockedCheckInDateSet = (blockedRanges) => {
  const checkInDates = new Set();
  blockedRanges.forEach((range) => {
    const startDate = new Date(range.check_in_date);
    if (Number.isNaN(startDate.getTime())) return;
    checkInDates.add(toInputDateValue(startDate));
  });
  return checkInDates;
};

export const buildListingAwareBlockedDateSet = (blockedRanges, syncSettings) => {
  const blockedDates = buildBlockedDateSet(blockedRanges);
  const preparationTimeNights = Number(syncSettings?.airbnbPreparationTimeNights || 0);

  blockedRanges.forEach((range) => {
    const startDate = new Date(range.check_in_date);
    const endDate = new Date(range.checkout_date);
    if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) return;

    if (preparationTimeNights > 0) {
      for (let dayOffset = 1; dayOffset <= preparationTimeNights; dayOffset += 1) {
        blockedDates.add(toInputDateValue(addDays(startDate, -dayOffset)));
      }
      for (let dayOffset = 0; dayOffset < preparationTimeNights; dayOffset += 1) {
        blockedDates.add(toInputDateValue(addDays(endDate, dayOffset)));
      }
    }
  });

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (syncSettings?.airbnbAdvanceNotice && syncSettings.airbnbAdvanceNotice !== 'same_day') {
    const noticeDays = Number(syncSettings.airbnbAdvanceNotice || 0);
    for (let dayOffset = 0; dayOffset < noticeDays; dayOffset += 1) {
      blockedDates.add(toInputDateValue(addDays(today, dayOffset)));
    }
  } else if (syncSettings?.airbnbAdvanceNotice === 'same_day') {
    const cutoffHour = Number(syncSettings.airbnbSameDayCutoffHour);
    const now = new Date();
    if (!Number.isNaN(cutoffHour) && now.getHours() >= cutoffHour) {
      blockedDates.add(toInputDateValue(today));
    }
  }

  return blockedDates;
};

export const rangeTouchesBlockedDates = (startValue, endValue, blockedDateSet) => {
  if (!startValue || !endValue) return false;
  const startDate = new Date(startValue);
  const endDate = new Date(endValue);
  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) return false;
  return datesWithinRange(startDate, endDate).some((dateValue) => blockedDateSet.has(dateValue));
};

export function PremiumRangeDatePicker({ checkInDate, checkoutDate, onChange, onClose, blockedDateSet, blockedCheckInDateSet }) {
  const initialDate = checkInDate ? new Date(checkInDate) : new Date();
  const [viewDate, setViewDate] = React.useState(new Date(initialDate.getFullYear(), initialDate.getMonth(), 1));

  const selectedStart = checkInDate ? new Date(checkInDate) : null;
  const selectedEnd = checkoutDate ? new Date(checkoutDate) : null;
  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const firstDay = (new Date(year, month, 1).getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const prevMonthDays = new Date(year, month, 0).getDate();
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const days = [];
  for (let i = firstDay - 1; i >= 0; i -= 1) {
    days.push({ key: `prev-${i}`, label: prevMonthDays - i, currentMonth: false });
  }
  for (let day = 1; day <= daysInMonth; day += 1) {
    const date = new Date(year, month, day);
    days.push({ key: `current-${day}`, label: day, currentMonth: true, date });
  }
  while (days.length % 7 !== 0) {
    days.push({ key: `next-${days.length}`, label: days.length % 7, currentMonth: false });
  }

  const isSelected = (date) => {
    if (!date) return false;
    const value = toInputDateValue(date);
    return value === checkInDate || value === checkoutDate;
  };
  const isCheckIn = (date) => date && checkInDate && toInputDateValue(date) === checkInDate;
  const isCheckOut = (date) => date && checkoutDate && toInputDateValue(date) === checkoutDate;
  const isToday = (date) => date && date.toDateString() === today.toDateString();
  const isInRange = (date) => {
    if (!selectedStart || !selectedEnd || !date) return false;
    return date > selectedStart && date < selectedEnd;
  };

  const handleDaySelect = (date) => {
    const nextValue = toInputDateValue(date);
    const isBlocked = blockedDateSet.has(nextValue);

    if (checkInDate && nextValue === checkInDate) {
      onChange({ checkInDate: '', checkoutDate: '' });
      return;
    }

    if (!checkInDate || nextValue < checkInDate) {
      if (isBlocked) {
        toast.error('Airbnb reservations cannot overlap these dates');
        return;
      }
      const autoCheckout = toInputDateValue(addDays(new Date(nextValue), 1));
      onChange({ checkInDate: nextValue, checkoutDate: autoCheckout });
      return;
    }

    if (rangeTouchesBlockedDates(checkInDate, nextValue, blockedDateSet)) {
      toast.error('Airbnb reservations cannot overlap these dates');
      return;
    }

    onChange({ checkInDate, checkoutDate: nextValue });
    onClose();
  };

  return (
    <motion.div initial={{ opacity: 0, y: 8, scale: 0.985 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: -6, scale: 0.985 }} transition={{ duration: 0.2, ease: [0.25, 0.1, 0.25, 1] }} className="premium-card" style={{ marginTop: 10, borderRadius: 26, padding: '18px 18px 14px', position: 'relative', zIndex: 20, boxShadow: 'var(--premium-shadow-nested)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
        <button type="button" onClick={() => setViewDate(new Date(year, month - 1, 1))} data-testid="host-calendar-prev-button" className="premium-card-compact premium-card-nested" style={{ width: 36, height: 36, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text)' }}><ChevronLeft size={18} color="currentColor" /></button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontFamily: "'Inria Serif', serif", fontSize: 24, fontWeight: 700 }}>{viewDate.toLocaleString('en-GB', { month: 'long', year: 'numeric' })}</span>
          <ChevronDown size={18} color="var(--text)" />
        </div>
        <button type="button" onClick={() => setViewDate(new Date(year, month + 1, 1))} data-testid="host-calendar-next-button" className="premium-card-compact premium-card-nested" style={{ width: 36, height: 36, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text)' }}><ChevronRight size={18} color="currentColor" /></button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 8, marginBottom: 10 }}>
        {WEEK_DAYS.map((label) => (
          <div key={label} style={{ textAlign: 'center', fontSize: 14, color: 'var(--text-2)', fontWeight: 700 }}>{label}</div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 8 }}>
        {days.map((day) => {
          const active = isSelected(day.date);
          const checkIn = isCheckIn(day.date);
          const checkOut = isCheckOut(day.date);
          const blocked = day.date ? blockedDateSet.has(toInputDateValue(day.date)) : false;
          const inRange = isInRange(day.date);
          const disabled = !day.currentMonth || !day.date || day.date < today;

          let border;
          let background;
          let color;
          let boxShadow = 'none';
          if (checkIn) {
            border = 'none';
            background = 'linear-gradient(135deg, var(--brand-red) 0%, var(--accent-purple-strong) 100%)';
            color = '#FFF';
            boxShadow = '0 6px 16px rgba(197, 18, 48, 0.35), inset 0 1px 0 rgba(255, 255, 255, 0.18)';
          } else if (checkOut) {
            border = '2px solid var(--brand-red)';
            background = 'transparent';
            color = 'var(--brand-red)';
            boxShadow = 'inset 0 0 0 3px rgba(229, 57, 53, 0.08)';
          } else if (inRange) {
            border = '1px solid rgba(229, 57, 53, 0.18)';
            background = 'var(--accent-surface-soft)';
            color = 'var(--text)';
          } else if (isToday(day.date)) {
            border = '2px solid #2FBF71';
            background = day.currentMonth ? 'var(--surface)' : 'var(--surface-2)';
            color = blocked ? '#D54141' : disabled ? 'var(--text-3)' : 'var(--text)';
          } else {
            border = '1px solid rgba(112, 63, 246, 0.08)';
            background = day.currentMonth ? 'var(--surface)' : 'var(--surface-2)';
            color = blocked ? '#D54141' : disabled ? 'var(--text-3)' : 'var(--text)';
          }

          return (
            <button
              type="button"
              key={day.key}
              disabled={disabled}
              onMouseDown={(event) => {
                if (disabled || !day.date) return;
                event.preventDefault();
                handleDaySelect(day.date);
              }}
              data-testid={day.currentMonth && day.date ? `host-range-day-${toInputDateValue(day.date)}` : undefined}
              className={isToday(day.date) && !checkIn && !checkOut ? 'host-calendar-today-breath' : undefined}
              style={{
                position: 'relative',
                border,
                background,
                color,
                borderRadius: 14,
                aspectRatio: '1',
                cursor: disabled ? 'default' : 'pointer',
                fontFamily: "'Inria Serif', serif",
                fontSize: 20,
                fontWeight: active ? 700 : 400,
                boxShadow,
                overflow: 'visible',
                zIndex: checkIn || checkOut ? 2 : 1,
              }}
            >
              {checkIn ? (
                <ArrowDownRight
                  size={16}
                  strokeWidth={3}
                  color="#2FBF71"
                  aria-hidden="true"
                  style={{
                    position: 'absolute',
                    top: -7,
                    left: -7,
                    filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.35))',
                  }}
                />
              ) : null}
              {checkOut ? (
                <ArrowUpRight
                  size={16}
                  strokeWidth={3}
                  color="#2FBF71"
                  aria-hidden="true"
                  style={{
                    position: 'absolute',
                    top: -7,
                    right: -7,
                    filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.35))',
                  }}
                />
              ) : null}
              {blocked && !checkIn && !checkOut ? (
                <span
                  aria-hidden="true"
                  style={{
                    display: 'inline-block',
                    width: 7,
                    height: 7,
                    borderRadius: '50%',
                    background: blockedCheckInDateSet && blockedCheckInDateSet.has(toInputDateValue(day.date))
                      ? '#2FBF71'
                      : '#D54141',
                  }}
                />
              ) : day.label}
            </button>
          );
        })}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16, gap: 10 }}>
        <button type="button" onClick={() => onChange({ checkInDate: '', checkoutDate: '' })} data-testid="host-range-clear-button" style={{ background: 'none', border: 'none', color: 'var(--accent-purple)', fontFamily: "'Inria Serif', serif", fontSize: 17, cursor: 'pointer' }}>Clear</button>
        <button
          type="button"
          onClick={onClose}
          data-testid="host-range-done-button"
          disabled={!checkInDate || !checkoutDate}
          style={{
            padding: '8px 22px',
            borderRadius: 999,
            border: 'none',
            background: (!checkInDate || !checkoutDate) ? 'var(--surface-2)' : 'linear-gradient(135deg, var(--brand-red) 0%, var(--accent-purple-strong) 100%)',
            color: (!checkInDate || !checkoutDate) ? 'var(--text-3)' : '#FFF',
            fontFamily: "'Inria Serif', serif",
            fontSize: 16,
            fontWeight: 700,
            cursor: (!checkInDate || !checkoutDate) ? 'default' : 'pointer',
            boxShadow: (!checkInDate || !checkoutDate) ? 'none' : '0 6px 18px rgba(197, 18, 48, 0.35)',
            transition: 'transform 160ms ease, box-shadow 160ms ease',
          }}
        >
          Done
        </button>
        <button type="button" onClick={() => { const todayValue = toInputDateValue(new Date()); const nextDay = toInputDateValue(addDays(new Date(todayValue), 1)); onChange({ checkInDate: todayValue, checkoutDate: nextDay }); }} data-testid="host-range-today-button" style={{ background: 'none', border: 'none', color: 'var(--accent-purple)', fontFamily: "'Inria Serif', serif", fontSize: 17, cursor: 'pointer' }}>Today</button>
      </div>
    </motion.div>
  );
}

// Avoid "unused import" false positive — AnimatePresence not used in this
// module, consumer wraps the picker with its own AnimatePresence.
export { AnimatePresence };
